import { FC, ReactElement, ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { Typography } from "@mui/material";
import { TabContext } from "@mui/lab";

import {
  DEFAULT_PROFILE_SETTINGS_TAB,
  PROFILE_SETTINGS_TAB_IDS,
  PROFILE_SETTINGS_TAB_MESSAGE_IDS,
  ProfileSettingsTabId,
} from "../constants";
import {
  StyledProfileSettingsLayout,
  StyledProfileTab,
  StyledProfileTabList,
  StyledProfileTabPanelContent,
  StyledProfileTabPanelScroll,
  StyledProfileTabsContentArea,
} from "../styled";
import { UserVersion } from "@framework/provider-user";

export interface IProfileSettingsTabsProps {
  appVersion: UserVersion;
  activeTab: ProfileSettingsTabId;
  onTabChange: (tab: ProfileSettingsTabId) => void;
  panels: Record<ProfileSettingsTabId, ReactNode>;
}

export const ProfileSettingsTabs: FC<IProfileSettingsTabsProps> = ({
  appVersion,
  activeTab,
  onTabChange,
  panels,
}): ReactElement => {
  const tabs = PROFILE_SETTINGS_TAB_IDS.filter(tab => {
    if (appVersion === UserVersion.BASE || appVersion === UserVersion.ADVANCED) {
      if (tab === "speed" || tab === "multiAnalysis") return false;
    }

    return true;
  });

  const resolvedTab = tabs.includes(activeTab) ? activeTab : DEFAULT_PROFILE_SETTINGS_TAB;

  return (
    <TabContext value={resolvedTab}>
      <StyledProfileSettingsLayout>
        <StyledProfileTabList
          orientation="vertical"
          variant="scrollable"
          onChange={(_event, value: string) => {
            onTabChange(value as ProfileSettingsTabId);
          }}
          aria-label="profile settings tabs"
        >
          {tabs.map(tabId => (
            <StyledProfileTab
              key={tabId}
              id={`profile-settings-tab-${tabId}`}
              value={tabId}
              label={<FormattedMessage id={PROFILE_SETTINGS_TAB_MESSAGE_IDS[tabId]} />}
            />
          ))}
        </StyledProfileTabList>

        <StyledProfileTabsContentArea
          role="tabpanel"
          id={`profile-settings-tabpanel-${resolvedTab}`}
          aria-labelledby={`profile-settings-tab-${resolvedTab}`}
        >
          <StyledProfileTabPanelScroll>
            <StyledProfileTabPanelContent key={resolvedTab}>{panels[resolvedTab]}</StyledProfileTabPanelContent>
          </StyledProfileTabPanelScroll>
        </StyledProfileTabsContentArea>
      </StyledProfileSettingsLayout>
    </TabContext>
  );
};

export const ProfileSettingsSectionTitle: FC<{ messageId: string }> = ({ messageId }): ReactElement => (
  <Typography variant="subtitle1" sx={{ mt: 0, mb: 1.5, fontWeight: 600 }}>
    <FormattedMessage id={messageId} />
  </Typography>
);
